import React, { useEffect, useRef, useState } from "react";
import { InputBase } from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  FileCheck2,
  ImagePlus,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";

const inputSx = {
  fontSize: "14px",
  fontWeight: 700,
  color: "#0f172a",
  "& input::placeholder": {
    color: "#94a3b8",
    opacity: 1,
  },
};

const FieldShell = ({ label, required, icon: Icon, children }) => {
  return (
    <div>
      <label className="doctor-form-label">
        {Icon ? <Icon size={14} /> : null}
        {label}
        {required ? <span>*</span> : null}
      </label>

      <div className="doctor-form-control">{children}</div>
    </div>
  );
};

const StepTwoVerification = ({
  formData,
  setFormData,
  nextStep,
  prevStep,
  uploadIMG,
  setLiscencePhoto,
}) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [img_loader, setImageLoader] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImageLoader(true);
    setError("");

    uploadIMG(file)
      .then((url) => {
        if (url) {
          setLiscencePhoto(url);
          setPreview(URL.createObjectURL(file));
        } else {
          setLiscencePhoto(null);
          setPreview(null);
        }
      })
      .finally(() => {
        setImageLoader(false);
      });
  };

  const handleNext = () => {
    if (!formData.mdcn || !formData.nin) {
      setError("Please fill all required fields");
      return;
    }

    if (!preview) {
      setError("Please upload your medical license");
      return;
    }

    nextStep();
  };

  useEffect(() => {
    return () => preview && URL.revokeObjectURL(preview);
  }, [preview]);

  return (
    <div className="doctor-step-body">
      <div className="doctor-field-grid">
        <FieldShell label="MDCN number" required icon={BadgeCheck}>
          <InputBase
            fullWidth
            placeholder="Enter your MDCN number"
            value={formData.mdcn || ""}
            onChange={(e) => handleChange("mdcn", e.target.value)}
            sx={inputSx}
          />
        </FieldShell>

        <FieldShell label="NIN number" required icon={LockKeyhole}>
          <InputBase
            fullWidth
            placeholder="Enter your NIN"
            value={formData.nin || ""}
            onChange={(e) => handleChange("nin", e.target.value)}
            sx={inputSx}
          />
        </FieldShell>
      </div>

      <div className="doctor-upload-group">
        <label className="doctor-form-label">
          <FileCheck2 size={14} />
          Medical licence
          <span>*</span>
        </label>

        <div
          onClick={() => !img_loader && fileInputRef.current?.click()}
          className={`doctor-upload-box ${preview ? "has-preview" : ""} ${
            img_loader ? "is-loading" : ""
          }`}
        >
          {preview ? (
            <div className="doctor-upload-preview">
              <div className="doctor-upload-image-wrap">
                <img src={preview} alt="Medical licence preview" />
              </div>

              <div className="doctor-upload-hover">
                <ImagePlus size={15} />
                Change document
              </div>

              <div className="doctor-upload-status">
                <ShieldCheck size={15} />
                Medical licence uploaded
              </div>
            </div>
          ) : (
            <div className="doctor-upload-empty">
              <UploadCloud size={28} />
              <h3>Upload your medical licence</h3>
              <p>
                Use a clear photo or scan. The licence number and name should be
                readable.
              </p>
              <span>
                <ImagePlus size={15} />
                Browse files
              </span>
              <small>JPG or PNG recommended. Keep file size around 1MB.</small>
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />

          {img_loader && (
            <div className="doctor-upload-loader">
              <Loader2 size={30} className="animate-spin" />
              <p>Uploading document...</p>
              <small>Please wait</small>
            </div>
          )}
        </div>
      </div>

      {error && <div className="doctor-form-error">{error}</div>}

      <div className="doctor-note-card">
        <ShieldCheck size={18} />
        <div>
          <p>Your documents stay private</p>
          <small>
            These documents are used only to verify your doctor profile and are
            never shared publicly.
          </small>
        </div>
      </div>

      <div className="doctor-action-grid">
        <button
          type="button"
          onClick={prevStep}
          className="doctor-secondary-action"
        >
          <ArrowLeft size={17} />
          Back
        </button>

        <button
          type="button"
          onClick={handleNext}
          disabled={img_loader}
          className="doctor-primary-action"
        >
          Continue
          <ArrowRight size={17} />
        </button>
      </div>
    </div>
  );
};

export default StepTwoVerification;
